/**
 * The Bryq paragraph in an interview invite (Oct 2026).
 *
 * When the recruiter ticks "Send Bryq in parallel to this interview", the candidate gets two
 * emails: our invite and Bryq's own link. The invite says so, in TA's wording from the Email
 * Library (`booking-page/bryq-invite-note`, DE/EN). The paragraph goes in above the sign-off,
 * follows the tick both ways, and is never added twice.
 */
import { useEffect, useState } from 'react';
import { resolveEmailTemplate } from '../../services/emailTemplatesApi';

/** Used only when the Library cannot be read — the same text the Library was seeded with. */
const FALLBACK: Record<'de' | 'en', string> = {
  de: 'Zusätzlich erhältst du in Kürze eine separate E-Mail von unserem Partner Bryq mit einem kurzen Online-Assessment. Bitte schließe es vor unserem Gespräch ab.',
  en: 'You will also shortly receive a separate email from our partner Bryq with a short online assessment. Please complete it before our conversation.',
};

/** One read per language per page load — the Library text rarely changes mid-session. */
const cache: Partial<Record<'de' | 'en', Promise<string>>> = {};

/**
 * The paragraph in the dialog's language. Fetched up front so a tick never waits on it, and
 * the fallback stands in until (or if never) the Library answers. Plain state, not react-query,
 * so the dialogs need no query provider to render.
 */
export function useBryqNote(language: 'de' | 'en'): string {
  const [fromLibrary, setFromLibrary] = useState<Partial<Record<'de' | 'en', string>>>({});
  useEffect(() => {
    let live = true;
    cache[language] ??= resolveEmailTemplate({ category: 'booking-page', key: 'bryq-invite-note', lang: language })
      .then((t) => t.body.trim())
      .catch(() => '');
    void cache[language].then((text) => {
      if (live && text) setFromLibrary((prev) => ({ ...prev, [language]: text }));
    });
    return () => {
      live = false;
    };
  }, [language]);
  return fromLibrary[language] || FALLBACK[language];
}

const SIGN_OFF =
  /^(mit\s+)?(viele|vielen|beste|besten|liebe|lieben|herzliche|herzlichen|freundliche|freundlichen)?\s*grü(ß|ss)e|^(best|kind|warm)?\s*regards|^best,?$|^cheers|^thanks|^danke/i;

/**
 * `body` with the paragraph in (above the sign-off) or out. Idempotent both ways.
 *
 * Taking it OUT removes every version this module could have put in — the Library text and both
 * fallbacks — not only the current `note`. A recruiter can edit the text while the fallback stands
 * in and the Library text arrives afterwards; unticking must still leave no promise of a Bryq test
 * in the email. Putting it in first takes any of those out, so it is never there twice.
 */
export function withBryqNote(body: string, note: string, on: boolean): string {
  if (!note) return body;
  let without = body;
  for (const known of new Set([note, FALLBACK.de, FALLBACK.en])) {
    without = without.replace(`\n\n${known}`, '').replace(known, '');
  }
  if (!on) return without;
  const lines = without.split('\n');
  let at = -1;
  for (let i = lines.length - 1; i >= 0; i -= 1) {
    if (SIGN_OFF.test(lines[i].trim())) {
      at = i;
      break;
    }
  }
  if (at <= 0) return `${without.trimEnd()}\n\n${note}`;
  const before = lines.slice(0, at).join('\n').trimEnd();
  const after = lines.slice(at).join('\n');
  return `${before}\n\n${note}\n\n${after}`;
}
